/**
 * **In stages** (§8.2, Board 12; iPad portrait Board 15·B; phone Board 16
 * cell 4): one row per stage — its name, how much (a number and the `%` | `$`
 * control, the same one on every stage), what it **comes to**, when, and
 * which visit — then **Add a stage**, the sum chip and the rule in words.
 *
 * The list is sent whole on every change, because that is what the endpoint
 * takes: a stage has no identity outside its position. *Comes to* is read
 * from the plan's own invoices and the chip from its `stageSum`; switching a
 * stage between `%` and `$` carries the number across and flips the unit, and
 * nothing here multiplies a percentage (rule 3, D14).
 */
import { useState } from 'react'
import {
  BILLING_ROWS, BILLING_TAB, stageSumChip, usd, type MilestoneTrigger, type QuoteBillingPlanView,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { MenuSelect } from '../../components/menu-select.js'
import { DateField } from '../../components/date-field.js'
import { QtyInput } from '../../builder/rows/line-row.js'
import { IconClose, IconGrip, IconPlus } from '../../frame/icons.js'
import { LinkButton, TextBox, UnitToggle } from './parts.js'
import type { KindPanelProps } from './kind-panel.js'

interface Stage {
  name: string
  trigger: MilestoneTrigger
  dueOn: string | null
  pct: number | null
  amount: number | null
  progressPct: number | null
  visitId: string | null
}

const COLUMNS = 'grid grid-cols-[18px_176px_150px_98px_188px_minmax(0,1fr)_22px] gap-x-2.5'

/** What a new stage is called before anybody renames it. */
function defaultName(index: number): string {
  return ['Deposit', 'Balance'][index] ?? `Stage ${index + 1}`
}

export function Stages({ plan, visits, layout, busy, onPatch, dateless }: KindPanelProps) {
  const saved: Stage[] = plan.milestones.map((one) => ({
    name: one.name, trigger: one.trigger, dueOn: one.dueOn, pct: one.pct, amount: one.amount,
    progressPct: one.progressPct, visitId: one.visitId,
  }))
  const [dragging, setDragging] = useState<number | null>(null)
  const blend = saved.some((one) => one.amount !== null)
  const comesTo = (index: number) =>
    plan.invoices.find((row) => row.role === 'stage' && row.stage === index)?.amount ?? 0
  const onPlan = visits.filter((one) => plan.visitIds.includes(one.id))
  const choices = (onPlan.length > 0 ? onPlan : visits).map((one) => ({ value: one.id, label: one.name }))

  const save = (next: Stage[]) => void onPatch({ milestones: next })
  const edit = (index: number, patch: Partial<Stage>) =>
    save(saved.map((one, at) => (at === index ? { ...one, ...patch } : one)))
  const flip = (index: number, unit: 'pct' | 'flat') => {
    const one = saved[index]!
    const number = one.amount ?? one.pct ?? 0
    // The number carries across unchanged; only the unit flips (§8.2, D14).
    edit(index, unit === 'flat' ? { amount: number, pct: null } : { pct: Math.min(100, number), amount: null })
  }
  const add = () => save([...saved, {
    name: defaultName(saved.length),
    trigger: saved.length === 0 ? 'on_approval' : 'visit_completed',
    dueOn: null,
    // What is left of the 100, so three stages add up without anybody doing it.
    pct: Math.max(1, Math.round((100 - (plan.stageSum?.pct ?? 0)) * 10) / 10),
    amount: null,
    progressPct: null,
    visitId: null,
  }])
  const move = (from: number, to: number) => {
    if (from === to) return
    const next = [...saved]
    const [one] = next.splice(from, 1)
    next.splice(to, 0, one!)
    save(next)
  }

  const phone = layout === 'phone'
  const portrait = layout === 'portrait'

  const amountOf = (one: Stage, index: number, full = false) => (
    <span className={cn('flex items-center gap-1.5', full ? 'w-full' : 'w-[150px]')}>
      <QtyInput label={`${one.name} how much`} width={full ? 'w-full' : 'w-[58px]'}
        className={full ? 'min-w-0 flex-1' : undefined}
        value={one.amount ?? one.pct ?? 0}
        onCommit={(value) => {
          if (one.amount !== null) edit(index, { amount: value })
          else edit(index, { pct: Math.min(100, Math.max(0.1, value)) })
        }} />
      <UnitToggle label={`${one.name} as`} width={80} unit={one.amount !== null ? 'flat' : 'pct'}
        onChange={(unit) => flip(index, unit)} />
    </span>
  )
  const whenOf = (one: Stage, index: number, full = false) => (
    <span className={full ? 'w-full' : 'w-[188px]'}>
      <MenuSelect size="field" label={`${one.name} when`} value={one.trigger} disabled={busy} noSearch
        sheet={{ title: 'When' }}
        options={BILLING_ROWS.stageWhen.filter(([value]) => !dateless || value !== 'on_date')
          .map(([value, label]) => ({ value, label }))}
        onChange={(next) => {
          if (!next || next === one.trigger) return
          edit(index, {
            trigger: next as MilestoneTrigger,
            visitId: next === 'visit_completed' ? one.visitId : null,
            progressPct: next === 'progress' ? one.progressPct ?? 50 : null,
          })
        }} />
    </span>
  )
  const whichOf = (one: Stage, index: number, full = false) => {
    if (one.trigger === 'visit_completed') {
      return (
        <span className={full ? 'w-full' : 'w-[170px]'}>
          <MenuSelect size="field" label={`${one.name} which visit`} value={one.visitId} disabled={busy}
            placeholder={BILLING_TAB.whichVisit} sheet={{ title: BILLING_TAB.whichVisit }}
            options={choices}
            onChange={(visitId) => edit(index, { visitId })} />
        </span>
      )
    }
    if (one.trigger === 'on_date') {
      return (
        <span className={full ? 'w-full' : 'w-[170px]'}>
          <DateField size="field" display="long" label={`${one.name} date`} value={one.dueOn ?? ''}
            onChange={(next) => edit(index, { dueOn: next || null })} />
        </span>
      )
    }
    if (one.trigger === 'progress') {
      return (
        <QtyInput label={`${one.name} percent complete`} width={full ? 'w-full' : 'w-[104px]'} unit="% done"
          value={one.progressPct ?? 50}
          onCommit={(value) => edit(index, { progressPct: Math.min(100, Math.max(1, value)) })} />
      )
    }
    return <span />
  }
  const comes = (one: Stage, index: number) => (
    <span className="tnum text-right text-[13px] font-semibold text-text" data-comes-to="">
      {usd(comesTo(index))}
      {blend && one.amount === null && (
        <span className="block text-[10.5px] font-normal leading-[1.1] text-text-faint">{BILLING_ROWS.ofTheRest}</span>
      )}
    </span>
  )
  const remove = (one: Stage, index: number) => (
    <button type="button" aria-label={`Remove ${one.name}`} disabled={busy}
      onClick={() => save(saved.filter((_, at) => at !== index))}
      className="inline-flex shrink-0 text-text-faint hover:text-danger">
      <IconClose size={14} />
    </button>
  )
  const name = (one: Stage, index: number, width?: string) => (
    <TextBox label={`${BILLING_TAB.name} of stage ${index + 1}`} value={one.name} width={width} disabled={busy}
      onCommit={(next) => edit(index, { name: next })} />
  )
  const grip = (index: number) => ({
    draggable: true,
    onDragStart: () => setDragging(index),
    onDragOver: (event: React.DragEvent) => { if (dragging !== null) event.preventDefault() },
    onDrop: () => { if (dragging !== null) move(dragging, index); setDragging(null) },
    onDragEnd: () => setDragging(null),
  })

  const footer = (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      <LinkButton disabled={busy || saved.length >= 20} onClick={add}>
        <IconPlus size={13} /> {BILLING_ROWS.addStage}
      </LinkButton>
      {!phone && <StageSumChip plan={plan} />}
      {!phone && <span className="ml-auto text-[11.5px] text-text-faint">{BILLING_ROWS.stagesHint}</span>}
    </div>
  )

  if (phone) {
    return (
      <div className="flex flex-col gap-3" data-stages="">
        {saved.map((one, index) => (
          <div key={index} className="flex flex-col gap-2 rounded-[12px] border border-line bg-surface p-3.5"
            data-stage-card="">
            <span className="flex items-center justify-between">
              <b className="text-[13px] font-bold text-text">{BILLING_TAB.stageCard(index + 1)}</b>
              {remove(one, index)}
            </span>
            <Labelled text={BILLING_TAB.name}>{name(one, index)}</Labelled>
            <Labelled text={BILLING_TAB.howMuch}>{amountOf(one, index, true)}</Labelled>
            {/* A package has no money yet, so nothing to come to (§10.2, "no totals"). */}
            {!dateless && (
              <span className="text-[12.5px] text-text-muted">
                {BILLING_TAB.comesTo} <b className="tnum font-bold text-text">{usd(comesTo(index))}</b>
                {blend && one.amount === null && <> · {BILLING_ROWS.ofTheRest}</>}
              </span>
            )}
            <Labelled text="When">{whenOf(one, index, true)}</Labelled>
            {one.trigger !== 'on_approval' && one.trigger !== 'job_completed' && (
              <Labelled text={one.trigger === 'visit_completed' ? BILLING_TAB.whichVisit : 'Which one'}>
                {whichOf(one, index, true)}
              </Labelled>
            )}
          </div>
        ))}
        {footer}
      </div>
    )
  }

  if (portrait) {
    return (
      <div className="flex flex-col gap-2.5" data-stages="">
        {saved.map((one, index) => (
          <div key={index} {...grip(index)} className="flex flex-col gap-2 border-b border-line-soft pb-2.5"
            data-stage-row="">
            <div className="flex items-center gap-2.5">
              <span className="cursor-grab text-text-faint"><IconGrip size={13} /></span>
              <span className="min-w-0 flex-1">{name(one, index)}</span>
              {amountOf(one, index)}
              <span className="w-[98px]">{comes(one, index)}</span>
              {remove(one, index)}
            </div>
            <div className="flex items-center gap-2.5 pl-[28px]">
              {whenOf(one, index)}
              {whichOf(one, index)}
            </div>
          </div>
        ))}
        {footer}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2.5" data-stages="">
      <div>
        <div className={cn(COLUMNS, 'pb-1.5')}>
          <span />
          {BILLING_ROWS.stageColumns.map((column, index) => (
            <span key={column} className={cn('text-[10.5px] font-semibold uppercase tracking-[0.06em]',
              'text-text-faint', index === 2 && 'text-right')}>{column}</span>
          ))}
          <span />
        </div>
        {saved.map((one, index) => (
          <div key={index} {...grip(index)} className={cn(COLUMNS, 'items-center py-[3px]')} data-stage-row="">
            <span className="cursor-grab text-text-faint"><IconGrip size={13} /></span>
            {name(one, index, 'w-[176px]')}
            {amountOf(one, index)}
            {comes(one, index)}
            {whenOf(one, index)}
            {whichOf(one, index)}
            {remove(one, index)}
          </div>
        ))}
      </div>
      {footer}
    </div>
  )
}

function Labelled({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <span className="flex flex-col gap-1">
      <span className="text-[12.5px] text-text-muted">{text}</span>
      {children}
    </span>
  )
}

/** The sum chip (§8.2's nine states): green when it adds up, amber when not, red past the job. */
export function StageSumChip({ plan }: { plan: Pick<QuoteBillingPlanView, 'stageSum'> }) {
  const chip = stageSumChip(plan)
  if (chip.state === 'empty') return null
  return (
    <span data-stage-sum={chip.state}
      className={cn('inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-[6px] px-[7px]',
        'text-[12px] font-semibold',
        chip.state === 'ok' ? 'bg-accent-soft text-accent-dark'
          : chip.state === 'exceeds' ? 'bg-danger-bg text-danger' : 'bg-warn-bg text-warn')}>
      {chip.text}
    </span>
  )
}
